import { Box, Skeleton, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Bar, Brush, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Chip from '/@/components/bayer/chip';
import colors from '/@/components/theme-provider/colors';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import useReportGraphics from '../../../../hooks/report-graphics';
import CropMonitoringResultGraphic, { CustomTooltipRow } from '../common';

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        value: number;
    }>;
    label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    const units = useUnitProvider();
    if (active && payload && payload.length) {
        const value = payload[0].value;
        return (
            <Box
                sx={{
                    background: 'white',
                    padding: 1,
                    borderRadius: 1,
                    border: (t: Theme) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Typography color='primary'>{label}</Typography>
                <CustomTooltipRow
                    label={_('Lluvias')}
                    value={value}
                    color='#89D329'
                    unit={units.getUnit('rain', 'corn')}
                />
            </Box>
        );
    }

    return null;
};

interface PrecipitationReportGraphicProps {
    cropMonitoring: unknown;
    historicalWeather: unknown;
    unitsProp: unknown;
    isPdf: boolean;
    width?: number;
    showOnlyGraphic?: boolean;
}

const PrecipitationReportGraphic = ({
    cropMonitoring,
    historicalWeather,
    unitsProp,
    isPdf,
    width,
    showOnlyGraphic = false,
}: PrecipitationReportGraphicProps) => {
    const { filters, filter, setFilter, data } = useReportGraphics(cropMonitoring, historicalWeather, unitsProp);
    const units = useUnitProvider(unitsProp);
    const crop = cropMonitoring?.crop;

    const renderChart = () => {
        const chartContent = (
            <>
                <XAxis
                    dataKey='date'
                    tickLine={false}
                    axisLine={false}
                    interval='preserveStartEnd'
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: colors.gray.default,
                    }}
                />
                <YAxis
                    tickLine={false}
                    axisLine={false}
                    interval='preserveStartEnd'
                    type='number'
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: colors.gray.default,
                    }}
                    tickFormatter={(value) => `${value} ${units.getUnit('rain', 'corn')}`}
                />
                {!isPdf && <Tooltip content={<CustomTooltip />} />}
                <Bar dataKey='precipAccum' fill='#89D329' radius={[8, 8, 0, 0]} />

                {!isPdf && data.length > 12 && (
                    <Brush
                        dataKey='date'
                        height={30}
                        stroke={colors.themes[settings.app.theme].secondary.default}
                        endIndex={11}
                        tickFormatter={() => ''}
                    />
                )}
            </>
        );

        return isPdf ? (
            <Box
                sx={{
                    '& svg': {
                        overflow: 'visible',
                    },
                }}
            >
                <ComposedChart
                    width={width}
                    height={400}
                    data={data}
                    margin={{ top: 0, right: 0, left: -10, bottom: 0 }}
                >
                    {chartContent}
                </ComposedChart>
            </Box>
        ) : (
            <ResponsiveContainer height={400}>
                <ComposedChart data={data} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                    {chartContent}
                </ComposedChart>
            </ResponsiveContainer>
        );
    };

    if (showOnlyGraphic && historicalWeather) {
        return <div style={{ width: '100%', height: '400px' }}>{renderChart()}</div>;
    }

    return (
        <CropMonitoringResultGraphic
            title={_('Precipitaciones')}
            description={_('Revisá la distribución de lluvias a lo largo de la campaña.')}
            references={[{ label: _('Lluvias'), color: '#89D329' }]}
        >
            {!isPdf && crop === 'corn' && (
                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                    {objectEntries(filters).map(([key, { name }]) => (
                        <Chip key={key} label={name} onClick={() => setFilter(key)} selected={filter === key} />
                    ))}
                </Stack>
            )}
            {!historicalWeather ? (
                <Skeleton variant='rectangular' height='400px' />
            ) : (
                <div style={{ width: '100%', height: '400px' }}>{renderChart()}</div>
            )}
        </CropMonitoringResultGraphic>
    );
};

export default PrecipitationReportGraphic;
